import { useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { ChevronDown, GripVertical, Pin, Search, X } from 'lucide-react'
import { Button } from '../ui/Button'
import { EXPORT_FORMATS } from './tableExport'
import { countFilters, emptyFilterValues } from './tableFilters'
import { formatNumber } from '../../lib/format'

/* ============================================================
   Shared overlay chrome
   ============================================================ */

/** Full-screen dimmer behind a drawer / modal. */
function Backdrop({ onClick }) {
  return (
    <div
      className="fixed inset-0 z-[70] bg-black/40 backdrop-blur-[1px] animate-[fadeIn_.12s_ease]"
      onClick={onClick}
      aria-hidden="true"
    />
  )
}

/** Close the overlay on Escape. */
function useEscape(onClose) {
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [onClose])
}

/* ============================================================
   Column manager — popover anchored under the "Columns" button
   ============================================================ */

export function ColumnManager({
  anchorRect,
  columns, // arranged: every col + {on, pinned, hideable, pinnable}
  onChangeOrder, // (keysInOrder) => void
  onChangeHidden, // (hiddenKeys) => void
  onTogglePin, // (key) => void
  onReset,
  onClose,
}) {
  useEscape(onClose)
  const [draft, setDraft] = useState(() => columns.map((c) => ({ ...c })))
  const [query, setQuery] = useState('')
  const dragIndex = useRef(null)
  const panelRef = useRef(null)

  /* Keep the popup in step with the columns it is editing.
   *
   * `draft` is seeded by a lazy initialiser that runs ONCE at mount, and this
   * component is never remounted while open. Reset therefore re-derived the
   * TABLE correctly while every checkbox in the popup kept its old value
   * (defect DF_021 / Mantis 22174). Syncing on the incoming prop also covers
   * any other outside change — a saved view, a preference load.
   *
   * Guarded by a shallow signature so a re-render with an equivalent array
   * can't clobber an in-progress drag. `commit` writes upward and the parent
   * echoes the same shape straight back, so without the guard every toggle
   * would round-trip through this effect. */
  const columnsSig = columns
    .map((c) => `${c.key}:${c.on ? 1 : 0}:${c.pinned ? 1 : 0}`)
    .join('|')
  useEffect(() => {
    setDraft((prev) => {
      const prevSig = prev
        .map((c) => `${c.key}:${c.on ? 1 : 0}:${c.pinned ? 1 : 0}`)
        .join('|')
      return prevSig === columnsSig ? prev : columns.map((c) => ({ ...c }))
    })
    // `columns` is a fresh array each render; the signature is the real input.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [columnsSig])

  // Close on outside click.
  useEffect(() => {
    const onDown = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) onClose()
    }
    const t = setTimeout(
      () => document.addEventListener('mousedown', onDown),
      0,
    )
    return () => {
      clearTimeout(t)
      document.removeEventListener('mousedown', onDown)
    }
  }, [onClose])

  const visibleCount = draft.filter((c) => c.on).length
  const allOn = draft.every((c) => c.on || !c.hideable)

  function commit(next) {
    setDraft(next)
    onChangeOrder(next.map((c) => c.key))
    onChangeHidden(next.filter((c) => !c.on).map((c) => c.key))
  }

  function toggle(key) {
    const next = draft.map((c) => ({ ...c }))
    const col = next.find((c) => c.key === key)
    if (!col.hideable) return
    if (col.on && visibleCount === 1) return // keep at least one
    col.on = !col.on
    commit(next)
  }

  function toggleAll() {
    const target = !allOn
    const next = draft.map((c) => ({
      ...c,
      on: c.hideable ? target : true,
    }))
    if (!target) next[0].on = true // never hide everything
    commit(next)
  }

  function onDrop(toIndex) {
    const from = dragIndex.current
    dragIndex.current = null
    if (from == null || from === toIndex) return
    const next = draft.map((c) => ({ ...c }))
    const [moved] = next.splice(from, 1)
    next.splice(toIndex, 0, moved)
    commit(next)
  }

  const filtered = draft.filter((c) =>
    c.header.toLowerCase().includes(query.trim().toLowerCase()),
  )

  /* Position, collision-aware.
   *
   * This used to be `top: anchorRect.bottom + 8` with no flip and no height
   * cap, so a Column button near the foot of the page opened a panel whose
   * Select-all row, hint and Reset/Done footer fell off the bottom of the
   * screen — and a `position: fixed` element cannot be scrolled into view
   * (defect DF_019 / Mantis 22167). Now it opens UPWARD when there isn't room
   * below, and the panel itself is capped and scrollable as a last resort
   * (matching ExportModal, which already carried `max-h` + `overflow-y-auto`). */
  const GAP = 8
  const MARGIN = 12
  const viewportH = typeof window === 'undefined' ? 800 : window.innerHeight
  const viewportW = typeof window === 'undefined' ? 1200 : window.innerWidth
  const spaceBelow = anchorRect ? viewportH - anchorRect.bottom - GAP - MARGIN : viewportH
  const spaceAbove = anchorRect ? anchorRect.top - GAP - MARGIN : 0
  // Flip only when there is genuinely more room above — a short panel that
  // already fits below should never jump.
  const flipUp = Boolean(anchorRect) && spaceBelow < 320 && spaceAbove > spaceBelow
  const style = anchorRect
    ? {
        ...(flipUp
          ? { bottom: Math.max(MARGIN, viewportH - anchorRect.top + GAP) }
          : { top: anchorRect.bottom + GAP }),
        right: Math.max(MARGIN, viewportW - anchorRect.right),
        maxHeight: Math.max(220, (flipUp ? spaceAbove : spaceBelow)),
      }
    : { top: 80, right: 24, maxHeight: viewportH - 160 }

  return createPortal(
    <>
      <Backdrop onClick={onClose} />
      <div
        ref={panelRef}
        role="dialog"
        aria-label="Choose columns"
        // flex column + the inline maxHeight above: the column list is the only
        // part allowed to shrink, so the search box, Select all and the
        // Reset/Done footer stay reachable at any viewport height.
        className="fixed z-[71] w-72 flex flex-col rounded-xl border border-[color:var(--color-rule)] bg-[color:var(--color-card)] shadow-xl p-3"
        style={style}
      >
        <div className="relative mb-2 shrink-0">
          <Search
            size={14}
            className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[color:var(--color-ink-muted)]"
          />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            // Client-side column filter; capped for consistency with the
            // other search fields.
            maxLength={200}
            placeholder="Search columns"
            className="w-full h-8 pl-8 pr-2 rounded-md border border-[color:var(--color-rule)] bg-[color:var(--color-card)] text-sm text-[color:var(--color-ink)] outline-none focus:border-[color:var(--color-accent)]"
          />
        </div>

        <label className="shrink-0 flex items-center gap-2 px-1 py-1.5 text-sm text-[color:var(--color-ink-strong)] cursor-pointer">
          <input type="checkbox" checked={allOn} onChange={toggleAll} />
          Select all
          <span className="ml-auto text-xs text-[color:var(--color-ink-muted)] tabular-nums">
            {visibleCount}/{draft.length}
          </span>
        </label>

        {/* The only flexible child: it absorbs whatever height is left after
            the fixed header and footer, so the footer is always on screen. */}
        <ul className="flex-1 min-h-0 max-h-72 overflow-y-auto py-1">
          {filtered.map((c) => {
            const realIndex = draft.findIndex((d) => d.key === c.key)
            return (
              <li
                key={c.key}
                draggable
                onDragStart={() => (dragIndex.current = realIndex)}
                onDragOver={(e) => e.preventDefault()}
                onDrop={() => onDrop(realIndex)}
                className="group flex items-center gap-2 px-1 py-1 rounded-md hover:bg-[color:var(--color-card-hover)]"
              >
                <GripVertical
                  size={14}
                  className="text-[color:var(--color-ink-subtle)] cursor-grab shrink-0"
                />
                <label className="flex items-center gap-2 text-sm text-[color:var(--color-ink)] cursor-pointer flex-1 min-w-0">
                  <input
                    type="checkbox"
                    checked={c.on}
                    disabled={!c.hideable}
                    onChange={() => toggle(c.key)}
                  />
                  <span className="truncate">{c.header}</span>
                </label>
                {c.pinnable ? (
                  <button
                    type="button"
                    onClick={() => onTogglePin(c.key)}
                    title={c.pinned ? 'Unfreeze column' : 'Freeze column'}
                    aria-pressed={c.pinned}
                    className={
                      // 24x24 minimum hit area (WCAG 2.5.8).
                      'inline-flex min-h-[24px] min-w-[24px] shrink-0 items-center justify-center rounded p-1 transition-colors ' +
                      (c.pinned
                        ? 'text-[color:var(--color-accent-strong)] bg-[color:var(--color-accent-bg)]'
                        : 'text-[color:var(--color-ink-subtle)] hover:text-[color:var(--color-ink)]')
                    }
                  >
                    <Pin size={13} />
                  </button>
                ) : (
                  <span className="w-[25px] shrink-0" />
                )}
              </li>
            )
          })}
        </ul>

        <p className="shrink-0 px-1 pt-1 text-xs text-[color:var(--color-ink-subtle)]">
          Drag to reorder. At least one column stays visible. Changes apply
          straight away.
        </p>
        <div className="shrink-0 mt-2 flex justify-between gap-2 border-t border-[color:var(--color-rule)] pt-2">
          <Button variant="ghost" size="sm" onClick={onReset}>
            Reset
          </Button>
          {/* "Close", not "Done".
           *
           * Every change here is applied the moment it is made — `commit`
           * writes through to the table on each toggle, select-all and drag,
           * and Escape / outside-click / backdrop all dismiss without
           * reverting. A button labelled "Done" implied a save step that does
           * not exist, so closing any other way looked like it should have
           * discarded the change (defect DF_020 / Mantis 22171). The label now
           * matches the behaviour, reinforced by the hint above. Switching to a
           * real draft-and-apply model is a product decision, not a rename. */}
          <Button variant="secondary" size="sm" onClick={onClose}>
            Close
          </Button>
        </div>
      </div>
    </>,
    document.body,
  )
}

/* ============================================================
   Filter drawer — declarative filter config
   ============================================================ */

/** Normalise a filter-values object to a comparable draft. */
function cloneValues(v) {
  return JSON.parse(JSON.stringify(v ?? {}))
}

export function FilterDrawer({
  config, // [{ key, label, type:'checkbox'|'daterange'|'text', options?, hint? }]
  values, // current applied values
  previewCount, // optional (draft) => number  (client mode only)
  onApply, // (values) => void
  onClose,
}) {
  useEscape(onClose)
  const [draft, setDraft] = useState(() => cloneValues(values))

  function setField(key, val) {
    setDraft((d) => ({ ...d, [key]: val }))
  }
  function toggleCheckbox(key, value) {
    setDraft((d) => {
      const cur = Array.isArray(d[key]) ? d[key] : []
      return {
        ...d,
        [key]: cur.includes(value)
          ? cur.filter((x) => x !== value)
          : [...cur, value],
      }
    })
  }

  const appliedCount = useMemo(() => countFilters(config, draft), [config, draft])
  const preview =
    typeof previewCount === 'function' ? previewCount(draft) : null

  function reset() {
    setDraft(emptyFilterValues(config))
  }

  // date-range validity: from must not be after to
  const dateInvalid = config.some((f) => {
    if (f.type !== 'daterange') return false
    const r = draft[f.key] || {}
    return r.from && r.to && r.from > r.to
  })

  return createPortal(
    <>
      <Backdrop onClick={onClose} />
      <aside
        role="dialog"
        aria-modal="true"
        aria-label="Filters"
        className="fixed right-0 top-0 z-[71] flex h-full w-full max-w-sm flex-col bg-[color:var(--color-card)] shadow-2xl animate-[slideIn_.16s_ease]"
      >
        <header className="flex items-center justify-between border-b border-[color:var(--color-rule)] px-5 py-4">
          <div>
            <h2 className="font-display text-lg text-[color:var(--color-ink-strong)] m-0">
              Filters
            </h2>
            <p className="text-xs text-[color:var(--color-ink-muted)] m-0 mt-0.5">
              <span className="tabular-nums">{appliedCount}</span> applied
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close filters"
            className="rounded-md p-1.5 text-[color:var(--color-ink-muted)] hover:bg-[color:var(--color-card-hover)] cursor-pointer"
          >
            <X size={18} />
          </button>
        </header>

        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-6">
          {config.map((f) => (
            <div key={f.key}>
              <span className="block text-xs font-semibold uppercase tracking-wide text-[color:var(--color-ink-muted)] mb-2">
                {f.label}
              </span>

              {f.type === 'checkbox' && (
                <div className="space-y-1.5">
                  {f.options.map((o) => (
                    <label
                      key={o.value}
                      className="flex items-center gap-2 text-sm text-[color:var(--color-ink)] cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        checked={(draft[f.key] || []).includes(o.value)}
                        onChange={() => toggleCheckbox(f.key, o.value)}
                      />
                      {o.label}
                    </label>
                  ))}
                </div>
              )}

              {f.type === 'radio' && (
                // max-h + scroll so a long, data-driven option list (e.g. every
                // department in a large tenant) stays a consistent inline radio
                // group instead of needing a different control type.
                <div className="space-y-1.5 max-h-56 overflow-y-auto pr-1">
                  <label className="flex items-center gap-2 text-sm text-[color:var(--color-ink)] cursor-pointer">
                    <input
                      type="radio"
                      name={`f-${f.key}`}
                      checked={!draft[f.key]}
                      onChange={() => setField(f.key, '')}
                    />
                    {f.anyLabel || 'Any'}
                  </label>
                  {f.options.map((o) => (
                    <label
                      key={o.value}
                      className="flex items-center gap-2 text-sm text-[color:var(--color-ink)] cursor-pointer"
                    >
                      <input
                        type="radio"
                        name={`f-${f.key}`}
                        checked={draft[f.key] === o.value}
                        onChange={() => setField(f.key, o.value)}
                      />
                      {o.label}
                    </label>
                  ))}
                </div>
              )}

              {f.type === 'select' && (
                <div className="relative">
                  <select
                    value={draft[f.key] || ''}
                    onChange={(e) => setField(f.key, e.target.value)}
                    className="w-full h-9 rounded-md border border-[color:var(--color-rule)] bg-[color:var(--color-card)] px-2.5 pr-8 text-sm text-[color:var(--color-ink)] outline-none focus:border-[color:var(--color-accent)] cursor-pointer appearance-none"
                  >
                    <option value="">{f.anyLabel || 'Any'}</option>
                    {f.options.map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                  <ChevronDown
                    size={14}
                    className="absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none text-[color:var(--color-ink-muted)]"
                  />
                </div>
              )}

              {f.type === 'daterange' && (
                <div className="grid grid-cols-2 gap-2">
                  <input
                    type="date"
                    aria-label={`${f.label} from`}
                    value={(draft[f.key] || {}).from || ''}
                    onChange={(e) =>
                      setField(f.key, {
                        ...(draft[f.key] || {}),
                        from: e.target.value,
                      })
                    }
                    className="h-9 rounded-md border border-[color:var(--color-rule)] bg-[color:var(--color-card)] px-2 text-sm text-[color:var(--color-ink)] outline-none focus:border-[color:var(--color-accent)]"
                  />
                  <input
                    type="date"
                    aria-label={`${f.label} to`}
                    value={(draft[f.key] || {}).to || ''}
                    onChange={(e) =>
                      setField(f.key, {
                        ...(draft[f.key] || {}),
                        to: e.target.value,
                      })
                    }
                    className="h-9 rounded-md border border-[color:var(--color-rule)] bg-[color:var(--color-card)] px-2 text-sm text-[color:var(--color-ink)] outline-none focus:border-[color:var(--color-accent)]"
                  />
                </div>
              )}

              {f.type === 'text' && (
                <input
                  type="text"
                  value={draft[f.key] || ''}
                  maxLength={f.maxLength || 200}
                  placeholder={f.placeholder || ''}
                  onChange={(e) => setField(f.key, e.target.value)}
                  className="w-full h-9 rounded-md border border-[color:var(--color-rule)] bg-[color:var(--color-card)] px-2.5 text-sm text-[color:var(--color-ink)] outline-none focus:border-[color:var(--color-accent)]"
                />
              )}

              {/* Validation error and helper text are now SEPARATE.
               *
               * Both used to share one muted `<p>`, so an invalid range ("To"
               * before "From") was reported in the same grey as ordinary hint
               * copy and did not read as an error at all (defect DF_030 /
               * Mantis 22233). Worse, the whole block hung off `f.hint &&`, so
               * a daterange filter declared without a hint suppressed the
               * validation message entirely. The error now renders on its own,
               * in the danger token, with role="alert". */}
              {(() => {
                const v = draft[f.key] || {}
                const rangeInvalid =
                  f.type === 'daterange' && v.from && v.to && v.from > v.to
                return (
                  <>
                    {rangeInvalid ? (
                      <p
                        role="alert"
                        className="mt-1 text-xs text-[color:var(--color-priority-urgent)]"
                      >
                        From must not be after To.
                      </p>
                    ) : null}
                    {f.hint && !rangeInvalid ? (
                      <p className="mt-1 text-xs text-[color:var(--color-ink-subtle)]">
                        {f.hint}
                      </p>
                    ) : null}
                  </>
                )
              })()}
            </div>
          ))}
        </div>

        <footer className="border-t border-[color:var(--color-rule)] px-5 py-4">
          {preview != null && (
            <p className="mb-3 text-sm text-[color:var(--color-ink-muted)]">
              <strong className="tabular-nums text-[color:var(--color-ink-strong)]">
                {formatNumber(preview)}
              </strong>{' '}
              records match
            </p>
          )}
          <div className="flex justify-between gap-2">
            <Button variant="ghost" size="sm" onClick={reset}>
              Reset
            </Button>
            <Button
              variant="primary"
              size="sm"
              disabled={dateInvalid}
              onClick={() => {
                onApply(draft)
                onClose()
              }}
            >
              Apply Filters
            </Button>
          </div>
        </footer>
      </aside>
    </>,
    document.body,
  )
}

/* ============================================================
   Export modal
   ============================================================ */

export function ExportModal({
  visibleColumnCount,
  totalColumnCount,
  pageCount,
  totalCount,
  selectedCount,
  onExport, // ({ scope, columns:'visible'|'all', format }) => void
  onClose,
  isoDate,
}) {
  useEscape(onClose)
  const [scope, setScope] = useState('page')
  const [colScope, setColScope] = useState('visible')
  const [format, setFormat] = useState('CSV')

  const fmt = EXPORT_FORMATS.find((f) => f.id === format)
  const recordN =
    scope === 'page'
      ? pageCount
      : scope === 'all'
        ? totalCount
        : selectedCount
  const colN = colScope === 'visible' ? visibleColumnCount : totalColumnCount
  const filename = `export${isoDate ? '-' + isoDate : ''}${fmt.ext}`

  const radio =
    'flex items-center gap-2 text-sm text-[color:var(--color-ink)] cursor-pointer py-1'

  return createPortal(
    <>
      <Backdrop onClick={onClose} />
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Export data"
        className="fixed left-1/2 top-1/2 z-[71] w-[min(92vw,560px)] max-h-[88vh] -translate-x-1/2 -translate-y-1/2 overflow-y-auto rounded-2xl border border-[color:var(--color-rule)] bg-[color:var(--color-card)] shadow-2xl"
      >
        <header className="px-6 pt-5 pb-3 border-b border-[color:var(--color-rule)]">
          <h2 className="font-display text-xl text-[color:var(--color-ink-strong)] m-0">
            Export Data
          </h2>
          <p className="text-sm text-[color:var(--color-ink-muted)] m-0 mt-0.5">
            Choose which records, columns and file format to export.
          </p>
        </header>

        <div className="px-6 py-4 space-y-5">
          <fieldset>
            <legend className="text-xs font-semibold uppercase tracking-wide text-[color:var(--color-ink-muted)] mb-1.5">
              Records to export
            </legend>
            <label className={radio}>
              <input
                type="radio"
                name="ex-rec"
                checked={scope === 'page'}
                onChange={() => setScope('page')}
              />
              Current page{' '}
              <em className="not-italic text-[color:var(--color-ink-muted)] tabular-nums">
                {formatNumber(pageCount)}
              </em>
            </label>
            <label className={radio}>
              <input
                type="radio"
                name="ex-rec"
                checked={scope === 'all'}
                onChange={() => setScope('all')}
              />
              All records{' '}
              <em className="not-italic text-[color:var(--color-ink-muted)] tabular-nums">
                {formatNumber(totalCount)}
              </em>
            </label>
            {selectedCount > 0 && (
              <label className={radio}>
                <input
                  type="radio"
                  name="ex-rec"
                  checked={scope === 'selected'}
                  onChange={() => setScope('selected')}
                />
                Selected records{' '}
                <em className="not-italic text-[color:var(--color-ink-muted)] tabular-nums">
                  {formatNumber(selectedCount)}
                </em>
              </label>
            )}
          </fieldset>

          <fieldset>
            <legend className="text-xs font-semibold uppercase tracking-wide text-[color:var(--color-ink-muted)] mb-1.5">
              Columns
            </legend>
            <label className={radio}>
              <input
                type="radio"
                name="ex-col"
                checked={colScope === 'visible'}
                onChange={() => setColScope('visible')}
              />
              Visible columns{' '}
              <em className="not-italic text-[color:var(--color-ink-muted)] tabular-nums">
                {visibleColumnCount}
              </em>
            </label>
            <label className={radio}>
              <input
                type="radio"
                name="ex-col"
                checked={colScope === 'all'}
                onChange={() => setColScope('all')}
              />
              All columns{' '}
              <em className="not-italic text-[color:var(--color-ink-muted)] tabular-nums">
                {totalColumnCount}
              </em>
            </label>
          </fieldset>

          <fieldset>
            <legend className="text-xs font-semibold uppercase tracking-wide text-[color:var(--color-ink-muted)] mb-1.5">
              File format
            </legend>
            <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
              {EXPORT_FORMATS.map((f) => (
                <label
                  key={f.id}
                  className={
                    'flex flex-col items-center justify-center rounded-lg border px-2 py-2.5 text-xs font-medium cursor-pointer transition-colors ' +
                    (format === f.id
                      ? 'border-[color:var(--color-accent)] bg-[color:var(--color-accent-bg)] text-[color:var(--color-accent-strong)]'
                      : 'border-[color:var(--color-rule)] text-[color:var(--color-ink)] hover:bg-[color:var(--color-card-hover)]')
                  }
                >
                  <input
                    type="radio"
                    name="ex-fmt"
                    className="sr-only"
                    checked={format === f.id}
                    onChange={() => setFormat(f.id)}
                  />
                  {f.id}
                </label>
              ))}
            </div>
            <p className="mt-2 text-xs text-[color:var(--color-ink-subtle)]">
              {fmt.note}
            </p>
          </fieldset>

          <p className="text-sm text-[color:var(--color-ink-muted)]">
            <strong className="tabular-nums text-[color:var(--color-ink-strong)]">
              {formatNumber(recordN)}
            </strong>{' '}
            records ×{' '}
            <strong className="tabular-nums text-[color:var(--color-ink-strong)]">
              {colN}
            </strong>{' '}
            columns →{' '}
            <strong className="text-[color:var(--color-ink-strong)]">
              {filename}
            </strong>
            <br />
            {/* Unconditional. Every export is generated in the browser and
             *  downloads straight away — `runExport` answers `downloaded` or
             *  `unsupported`, never `queued`, and nothing mails a file
             *  anywhere. This used to branch on `fmt.immediate` and promise
             *  "prepared on the server and emailed when ready" for Parquet /
             *  ZIP, which no code could deliver (DF_024 / Mantis 22201).
             *  Those formats are gone; the branch stayed behind and would
             *  resurrect the false promise the moment anyone added a format
             *  without `immediate: true`. If a real server-side exporter ever
             *  lands, reintroduce the message WITH the job that backs it. */}
            Downloads immediately.
          </p>
        </div>

        <footer className="flex justify-end gap-2 px-6 py-4 border-t border-[color:var(--color-rule)]">
          <Button variant="secondary" size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            size="sm"
            onClick={() => onExport({ scope, columns: colScope, format })}
          >
            Export
          </Button>
        </footer>
      </div>
    </>,
    document.body,
  )
}
